import { useCallback, useMemo } from 'react'

interface NavigableItem {
  id: string
}

interface TaskListNavigation {
  handleNavigateUp: () => void
  handleNavigateDown: () => void
  canNavigateUp: boolean
  canNavigateDown: boolean
}

export function useTaskListNavigation(
  tasks: ReadonlyArray<NavigableItem>,
  selectedTaskId: string | null,
  setSelectedTaskId: (id: string) => void,
): TaskListNavigation {
  const handleNavigateUp = useCallback(() => {
    if (!selectedTaskId) return
    const currentIndex = tasks.findIndex((item) => item.id === selectedTaskId)
    const previous = tasks[currentIndex - 1]
    if (currentIndex > 0 && previous) {
      setSelectedTaskId(previous.id)
    }
  }, [tasks, selectedTaskId, setSelectedTaskId])

  const handleNavigateDown = useCallback(() => {
    if (!selectedTaskId) return
    const currentIndex = tasks.findIndex((item) => item.id === selectedTaskId)
    const next = tasks[currentIndex + 1]
    if (currentIndex >= 0 && next) {
      setSelectedTaskId(next.id)
    }
  }, [tasks, selectedTaskId, setSelectedTaskId])

  const canNavigateUp = useMemo(() => {
    if (!selectedTaskId) return false
    const currentIndex = tasks.findIndex((item) => item.id === selectedTaskId)
    return currentIndex > 0
  }, [tasks, selectedTaskId])

  const canNavigateDown = useMemo(() => {
    if (!selectedTaskId) return false
    const currentIndex = tasks.findIndex((item) => item.id === selectedTaskId)
    return currentIndex >= 0 && currentIndex < tasks.length - 1
  }, [tasks, selectedTaskId])

  return { handleNavigateUp, handleNavigateDown, canNavigateUp, canNavigateDown }
}
